import { useEffect, useState } from "react";
import { Form, Col, Flex, Typography, Button, InputNumber, Tag } from "antd";
import { TbX, TbPlus, TbTrash, TbLayersIntersect, TbLock, TbLayersDifference } from "react-icons/tb";
import { ExtraFee } from "dtos/strategy.dto";
import { FormListProps } from "antd/es/form";

const LAWSUIT_CONDITION_LABEL: Record<string, string> = {
  [ExtraFee.ApplyCondition.HAS_LAWSUITS]: "Has Lawsuits",
  [ExtraFee.ApplyCondition.NO_LAWSUITS]: "No Lawsuits",
};

const STAGE = {
  initial: ExtraFee.ApplyCondition.IS_INITIAL,
  weekly: ExtraFee.ApplyCondition.NOT_INITIAL,
} as const;

type Stage = keyof typeof STAGE;

const isStageCondition = (c: ExtraFee.ApplyCondition) => c === STAGE.initial || c === STAGE.weekly;

const isLawsuitCondition = (c: ExtraFee.ApplyCondition) =>
  c === ExtraFee.ApplyCondition.HAS_LAWSUITS || c === ExtraFee.ApplyCondition.NO_LAWSUITS;

const buildFixed = (stage?: Stage): ExtraFee.Config => ({
  mode: ExtraFee.CalculationMode.FIXED,
  applyIf: stage ? [STAGE[stage]] : [],
  value: 0,
  meta: {},
});

const buildPerLawsuit = (stage?: Stage): ExtraFee.Config => ({
  mode: ExtraFee.CalculationMode.PER_LAWSUIT,
  applyIf: [...(stage ? [STAGE[stage]] : []), ExtraFee.ApplyCondition.HAS_LAWSUITS],
  value: 0,
  meta: {},
});

const buildFallback = (stage?: Stage): ExtraFee.Config => ({
  mode: ExtraFee.CalculationMode.FIXED,
  applyIf: [...(stage ? [STAGE[stage]] : []), ExtraFee.ApplyCondition.NO_LAWSUITS],
  value: 0,
  meta: { isDefaultFor: ExtraFee.CalculationMode.PER_LAWSUIT },
});

export type CustomParameters = "service_fee" | "legal_fee";
const customParams: Record<CustomParameters, { label: string }> = {
  service_fee: { label: "Service Fee" },
  legal_fee: { label: "Legal Fee" },
};

const ModifyParameters = ({
  name,
  slug,
  onRemove,
  collapse = "auto",
}: {
  name: FormListProps["name"];
  slug: CustomParameters;
  onRemove?: () => void;
  collapse?: boolean | "auto";
}) => {
  const { label } = customParams[slug];
  const form = Form.useFormInstance();
  const rules: ExtraFee.Config[] = Form.useWatch(name, form) ?? [];

  const hasStageTag = rules.some((r) => (r?.applyIf ?? []).some(isStageCondition));
  const [stageSplit, setStageSplit] = useState<boolean>(false);

  useEffect(() => {
    setStageSplit(hasStageTag);
  }, [hasStageTag]);

  return (
    <Col sm={collapse === true ? 12 : 24} md={collapse === false ? 24 : 12}>
      <Flex vertical gap={12} className="rounded-md p-3 px-4 border border-solid border-gray-300">
        <Flex className="justify-between" align="center">
          <Typography.Title level={5} style={{ margin: 0 }}>
            {label}
          </Typography.Title>
          <Flex>
            <Button size="small" type="text" color="blue" onClick={() => setStageSplit((prev) => !prev)}>
              {stageSplit ? <TbLayersDifference /> : <TbLayersIntersect />}
            </Button>
            <Button size="small" type="text" danger onClick={onRemove}>
              <TbX />
            </Button>
          </Flex>
        </Flex>

        <Form.List name={name}>
          {(fields, { add, remove }) => {
            const fieldsByStage = (stage: Stage) =>
              fields.filter((f) => (rules[f.name]?.applyIf ?? []).includes(STAGE[stage]));

            const looseFields = fields.filter((f) => !(rules[f.name]?.applyIf ?? []).some(isStageCondition));

            const removeWithCascade = (fieldName: number) => {
              const rule = rules[fieldName];
              if (rule?.mode === ExtraFee.CalculationMode.PER_LAWSUIT) {
                const stageOfRule = (rule.applyIf ?? []).find(isStageCondition);
                const fallbackFieldName = fields.find((f) => {
                  const r = rules[f.name];
                  if (!r) return false;
                  if (r.meta?.isDefaultFor !== ExtraFee.CalculationMode.PER_LAWSUIT) return false;
                  const ruleStage = (r.applyIf ?? []).find(isStageCondition);
                  return ruleStage === stageOfRule;
                })?.name;

                const toRemove = [fieldName, fallbackFieldName]
                  .filter((n): n is number => typeof n === "number")
                  .sort((a, b) => b - a);
                toRemove.forEach((n) => remove(n));
              } else {
                remove(fieldName);
              }
            };

            if (stageSplit) {
              return (
                <Flex vertical gap={12}>
                  <StageSection
                    title="Initial Payment"
                    color="#6366f1"
                    parentName={slug}
                    rules={rules}
                    fields={fieldsByStage("initial")}
                    onAddFixed={() => add(buildFixed("initial"))}
                    onAddPerLawsuit={() => add(buildPerLawsuit("initial"))}
                    onAddFallback={() => add(buildFallback("initial"))}
                    onRemove={removeWithCascade}
                  />
                  <StageSection
                    title="Weekly Payment"
                    color="#10b981"
                    parentName={slug}
                    rules={rules}
                    fields={fieldsByStage("weekly")}
                    onAddFixed={() => add(buildFixed("weekly"))}
                    onAddPerLawsuit={() => add(buildPerLawsuit("weekly"))}
                    onAddFallback={() => add(buildFallback("weekly"))}
                    onRemove={removeWithCascade}
                  />
                </Flex>
              );
            }

            return (
              <StageSection
                parentName={slug}
                rules={rules}
                fields={looseFields}
                onAddFixed={() => add(buildFixed())}
                onAddPerLawsuit={() => add(buildPerLawsuit())}
                onAddFallback={() => add(buildFallback())}
                onRemove={removeWithCascade}
              />
            );
          }}
        </Form.List>
      </Flex>
    </Col>
  );
};

const StageSection = ({
  title,
  color,
  parentName,
  rules,
  fields,
  onAddFixed,
  onAddPerLawsuit,
  onAddFallback,
  onRemove,
}: {
  title?: string;
  color?: string;
  parentName: string;
  rules: ExtraFee.Config[];
  fields: { key: number; name: number }[];
  onAddFixed: () => void;
  onAddPerLawsuit: () => void;
  onAddFallback: () => void;
  onRemove: (index: number) => void;
}) => {
  const sectionRules = fields.map((f) => rules[f.name]).filter(Boolean);
  const hasFixed = sectionRules.some((r) => r.mode === ExtraFee.CalculationMode.FIXED && !r.meta?.isDefaultFor);
  const hasPerLawsuit = sectionRules.some((r) => r.mode === ExtraFee.CalculationMode.PER_LAWSUIT);
  const hasFallback = sectionRules.some((r) => r.meta?.isDefaultFor === ExtraFee.CalculationMode.PER_LAWSUIT);

  const showFixedButton = !hasFixed && !hasPerLawsuit;
  const showPerLawsuitButton = !hasFixed && !hasPerLawsuit;
  const showFallbackButton = hasPerLawsuit && !hasFallback;

  return (
    <Flex
      vertical
      gap={8}
      style={
        title
          ? {
              padding: 10,
              borderRadius: 6,
              background: "#fafafa",
              borderLeft: `3px solid ${color}`,
            }
          : undefined
      }
    >
      {title && (
        <Tag color={color} style={{ alignSelf: "flex-start", margin: 0 }}>
          {title}
        </Tag>
      )}

      {fields.length === 0 && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          No rules yet — add one below.
        </Typography.Text>
      )}

      {fields.map((field, idx) => (
        <RuleItem
          key={field.key}
          field={field}
          index={idx}
          parentName={parentName}
          rule={rules[field.name]}
          onRemove={() => onRemove(field.name)}
        />
      ))}

      {(showFixedButton || showPerLawsuitButton || showFallbackButton) && (
        <Flex gap={6} wrap="wrap">
          {showFixedButton && (
            <Button size="small" icon={<TbPlus />} onClick={onAddFixed}>
              Fixed Amount
            </Button>
          )}
          {showPerLawsuitButton && (
            <Button size="small" icon={<TbPlus />} onClick={onAddPerLawsuit}>
              Per Lawsuit
            </Button>
          )}
          {showFallbackButton && (
            <Button size="small" icon={<TbPlus />} onClick={onAddFallback} type="dashed">
              No Lawsuit Fallback
            </Button>
          )}
        </Flex>
      )}
    </Flex>
  );
};

const RuleItem = ({
  field,
  index,
  parentName,
  rule,
  onRemove,
}: {
  field: { key: number; name: number };
  index: number;
  parentName: string;
  rule?: ExtraFee.Config;
  onRemove: () => void;
}) => {
  const mode = Form.useWatch([parentName, field.name, "mode"]) ?? rule?.mode;
  const isPerUnit = mode === ExtraFee.CalculationMode.PER_LAWSUIT;
  const isFallback = rule?.meta?.isDefaultFor === ExtraFee.CalculationMode.PER_LAWSUIT;
  const lockedLawsuitCondition = (rule?.applyIf ?? []).find(isLawsuitCondition);

  return (
    <Flex
      vertical
      gap={6}
      style={{
        padding: 8,
        background: "#fff",
        border: "1px solid #eee",
        borderRadius: 6,
      }}
    >
      <Flex justify="space-between" align="center">
        <Flex align="center" gap={6}>
          <Typography.Text strong style={{ fontSize: 11, color: "#6366f1" }}>
            Rule #{index + 1}
          </Typography.Text>
          {lockedLawsuitCondition && (
            <Tag icon={<TbLock style={{ marginRight: 2 }} />} color="default" style={{ margin: 0, fontSize: 10 }}>
              {LAWSUIT_CONDITION_LABEL[lockedLawsuitCondition]}
            </Tag>
          )}
          {isFallback && (
            <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>
              Fallback
            </Tag>
          )}
        </Flex>
        <Button size="small" type="text" danger icon={<TbTrash />} onClick={onRemove} />
      </Flex>

      <Flex gap={8} wrap="wrap" align="center">
        <Form.Item
          name={[field.name, "value"]}
          rules={[{ required: true, message: "Enter value" }]}
          style={{ flex: "1 1 120px", marginBottom: 0 }}
        >
          <InputNumber
            size="small"
            style={{ width: "100%" }}
            min={0}
            prefix="$"
            placeholder={isPerUnit ? "Per lawsuit" : "Amount"}
          />
        </Form.Item>
        {/* <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          {isPerUnit ? "× number of lawsuits" : "flat"}
        </Typography.Text> */}
      </Flex>

      <Form.Item name={[field.name, "mode"]} hidden />
      <Form.Item name={[field.name, "applyIf"]} hidden />
      <Form.Item name={[field.name, "meta"]} hidden />
    </Flex>
  );
};

export default ModifyParameters;
